import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.libraryIntelligenceApiUrl, {
  camelizeKeys: true,
});

/**
 * Get visual features string from image data
 *
 * Expects a base64 encoding for a jpg image (excluding MIME type).
 * Uses a HTML form content-type rather than JSON, per intelligence's endpoint.
 */
export async function vectoriseV2({ imageData: img }, config = {}) {
  return http.post(
    `/vectorise_v2`,
    { img },
    merge({ headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }, config),
  );
}

export async function detectTextLanguage({ texts }, config = {}) {
  return http.post(`/detect_language`, { texts }, config);
}

export async function translateTextLanguage({ texts }, config = {}) {
  return http.post(`/translate_language`, { texts }, config);
}

/**
 * @param {{ brandId: any, mediaId: any, numberOfMediaToGenerate?: number, referenceUrl?: string | null, parentAlternativeId?: number | null, mode?: string, socketId?: string }} params
 * @param {import('axios').AxiosRequestConfig} [config]
 */
export async function generateAiAlternatives(
  {
    brandId,
    mediaId,
    numberOfMediaToGenerate = 8,
    referenceUrl,
    parentAlternativeId = null,
    mode = 'creative',
    socketId,
  },
  config = {},
) {
  return http.post(
    `/ai_alternative_media`,
    {
      brandId,
      mediaId,
      numberOfMediaToGenerate,
      mode,
      ...(referenceUrl && { referenceUrl }),
      ...(parentAlternativeId && { parentId: parentAlternativeId }),
      ...(socketId && { socketId }),
    },
    config,
  );
}

export async function generateAiAlternativeAds(
  { brandId, adPlatform, sourceAdId, sourceCampaignId, sourceAdAccountId, thumbnailUrl, socketId },
  config = {},
) {
  return http.post(
    `/ai_alternative_ads`,
    {
      brandId,
      adPlatform,
      sourceAdId,
      sourceCampaignId,
      sourceAdAccountId,
      thumbnailUrl,
      mode: 'strict',
      ...(socketId && { socketId }),
    },
    config,
  );
}

/**
 * @param {Object} params
 * @param {number} params.brandId
 * @param {string} params.instruction
 * @param {string[]} [params.imageUrls]
 * @param {string} [params.sessionId]
 * @param {number} [params.replayFromTurn]
 * @param {string} [params.socketId] - Socket ID for async flow; when provided, returns 202 with sessionId
 * @param {import('axios').AxiosRequestConfig} [config]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function editAiImage(
  { brandId, instruction, imageUrls, sessionId, replayFromTurn, socketId },
  config = {},
) {
  return http.post(
    `/ai_image_edit`,
    {
      brandId,
      instruction,
      imageUrls,
      ...((env.mode === 'production' || env.mode === 'dashsocial') && { resolution: '2K' }),
      ...(sessionId && { sessionId }),
      ...(replayFromTurn && { replayFromTurn }),
      ...(socketId && { socketId }),
    },
    config,
  );
}

/**
 * @param {Object} params
 * @param {number} params.brandId
 * @param {string} params.instruction
 * @param {string} [params.imageUrl] - Required on turn 1 only
 * @param {string} [params.sessionId] - Omitted on turn 1, included on subsequent turns
 * @param {string} [params.imageEditSessionId] - Parent session ID for traceability
 * @param {string} params.socketId - Socket.IO connection ID for async result delivery
 * @param {import('axios').AxiosRequestConfig} [config]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function generateMotionVideo(
  { brandId, instruction, imageUrl, sessionId, imageEditSessionId, socketId },
  config = {},
) {
  return http.post(
    `/ai_video_gen`,
    {
      brandId,
      instruction,
      socketId,
      ...(imageUrl && { imageUrl }),
      ...(sessionId && { sessionId }),
      ...(imageEditSessionId && { parentSessionId: imageEditSessionId }),
    },
    config,
  );
}
